import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Save, FileText, Globe } from "lucide-react";

/**
 * Admin-only editor for the static content pages surfaced from the Home
 * (Missione and Chi siamo). Reachable at /admin/content-pages.
 *
 * Supports full multilingual editing: the admin fills each language tab
 * (IT / EN / FR / DE / ES / PT) independently. Italian is the base
 * fallback used when a requested language has no translation. No
 * automatic translation is performed — every field is admin-authored.
 */
const SLUGS = [
  { slug: "missione", label: "La nostra missione" },
  { slug: "chi-siamo", label: "Chi siamo" },
];

// Language codes in display order. IT is the base (mandatory fallback).
const LANGS = [
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
];

const emptyTranslation = () => ({ title: "", body_md: "", people: "", contacts: "" });

function PageEditor({ slug, label }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState(false);
  // Per-language translations map (kept fully in state, one entry per language).
  const [translations, setTranslations] = useState(
    LANGS.reduce((acc, l) => ({ ...acc, [l.code]: emptyTranslation() }), {})
  );
  const [activeLang, setActiveLang] = useState("it");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/pages/${slug}`);
        if (cancel) return;
        const t = data?.translations || {};
        // Backwards compatibility: if `translations.it` is missing but the
        // legacy top-level fields exist, promote them into `it` so the
        // admin can migrate the existing Italian content without loss.
        const legacyIt = (!t.it && (data?.title || data?.body_md || data?.people || data?.contacts))
          ? {
              title: data?.title || "",
              body_md: data?.body_md || "",
              people: data?.people || "",
              contacts: data?.contacts || "",
            }
          : null;
        setTranslations(
          LANGS.reduce((acc, l) => {
            const src = t[l.code] || (l.code === "it" ? legacyIt : null) || emptyTranslation();
            acc[l.code] = {
              title: src.title || "",
              body_md: src.body_md || "",
              people: src.people || "",
              contacts: src.contacts || "",
            };
            return acc;
          }, {})
        );
        setPublished(!!data?.published);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore nel caricamento");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  const setField = (lang, field, value) => {
    setTranslations((s) => ({ ...s, [lang]: { ...s[lang], [field]: value } }));
  };

  const save = async () => {
    // Only send non-empty translations (drop languages the admin left blank).
    const cleaned = {};
    for (const lc of Object.keys(translations)) {
      const obj = translations[lc] || {};
      const hasContent = (obj.title || "").trim()
        || (obj.body_md || "").trim()
        || (obj.people || "").trim()
        || (obj.contacts || "").trim();
      if (hasContent) {
        cleaned[lc] = {
          title: (obj.title || "").trim(),
          body_md: (obj.body_md || "").trim(),
          ...(slug === "chi-siamo" ? { people: (obj.people || "").trim(), contacts: (obj.contacts || "").trim() } : {}),
        };
      }
    }
    if (published && !cleaned.it) {
      toast.error("Compila almeno la versione italiana prima di pubblicare");
      return;
    }
    // Also keep the legacy flat top-level fields synced with the Italian
    // translation so any consumer still reading them gets the current copy.
    const it = cleaned.it || {};
    const payload = {
      published,
      translations: cleaned,
      title: it.title || "",
      body_md: it.body_md || "",
      ...(slug === "chi-siamo" ? { people: it.people || "", contacts: it.contacts || "" } : {}),
    };
    setSaving(true);
    try {
      await api.put(`/pages/${slug}`, payload);
      const langsSaved = Object.keys(cleaned).length;
      toast.success(`"${label}" salvata · ${langsSaved} lingua/e`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il salvataggio");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-500 italic p-4">Caricamento…</div>;

  const cur = translations[activeLang] || emptyTranslation();
  const isChiSiamo = slug === "chi-siamo";

  const langHasContent = (lc) => {
    const o = translations[lc] || {};
    return !!((o.title || "").trim() || (o.body_md || "").trim() || (o.people || "").trim() || (o.contacts || "").trim());
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 mb-4" data-testid={`admin-page-${slug}`}>
      <div className="flex items-center gap-2 mb-3">
        <FileText size={16} className="text-emerald-700" />
        <div className="font-heading font-bold text-slate-900 text-lg">{label}</div>
        <span className="ml-auto text-[11px] text-slate-500">slug: {slug}</span>
      </div>

      {/* Language tabs */}
      <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-1" data-testid={`admin-page-${slug}-langtabs`}>
        <Globe size={14} className="text-slate-400 mr-1 shrink-0" />
        {LANGS.map((l) => {
          const active = activeLang === l.code;
          const filled = langHasContent(l.code);
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => setActiveLang(l.code)}
              data-testid={`admin-page-${slug}-langtab-${l.code}`}
              className={`px-3 h-8 rounded-full text-xs font-semibold border shrink-0 inline-flex items-center gap-1.5 ${
                active
                  ? "bg-emerald-600 border-emerald-600 text-white"
                  : filled
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              aria-pressed={active}
            >
              <span className="uppercase">{l.code}</span>
              <span className="text-[10px] opacity-80">{l.label}</span>
              {filled && !active && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      <div className="grid gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            Titolo <span className="text-slate-400">· {activeLang.toUpperCase()}</span>
          </Label>
          <Input
            data-testid={`admin-page-${slug}-title-${activeLang}`}
            value={cur.title}
            onChange={(e) => setField(activeLang, "title", e.target.value)}
            placeholder={label}
            className="mt-1 rounded-lg"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            {isChiSiamo ? "Storia del progetto / testo introduttivo" : "Testo (sostituisce il default se pubblicato)"} <span className="text-slate-400">· {activeLang.toUpperCase()}</span>
          </Label>
          <Textarea
            data-testid={`admin-page-${slug}-body-${activeLang}`}
            value={cur.body_md}
            onChange={(e) => setField(activeLang, "body_md", e.target.value)}
            rows={12}
            className="mt-1 rounded-lg font-mono text-sm"
            placeholder={isChiSiamo ? "Racconta la storia di ToClean, chi ha dato vita al progetto…" : "Testo lungo (sarà mostrato al posto delle sezioni predefinite)"}
          />
          <div className="text-[11px] text-slate-500 mt-1">
            {activeLang === "it"
              ? "L'italiano è la lingua di base: se una lingua non è compilata, gli utenti vedranno il testo italiano."
              : "Lascia vuoto per usare il testo italiano come fallback per questa lingua."}
          </div>
        </div>
        {isChiSiamo && (
          <>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Persone coinvolte <span className="text-slate-400">· {activeLang.toUpperCase()}</span>
              </Label>
              <Textarea
                data-testid={`admin-page-${slug}-people-${activeLang}`}
                value={cur.people}
                onChange={(e) => setField(activeLang, "people", e.target.value)}
                rows={4}
                className="mt-1 rounded-lg text-sm"
                placeholder="Un nome/ruolo per riga (es. Mario Rossi — Fondatore)"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Contatti <span className="text-slate-400">· {activeLang.toUpperCase()}</span>
              </Label>
              <Textarea
                data-testid={`admin-page-${slug}-contacts-${activeLang}`}
                value={cur.contacts}
                onChange={(e) => setField(activeLang, "contacts", e.target.value)}
                rows={3}
                className="mt-1 rounded-lg text-sm"
                placeholder="Email, PEC, indirizzo, social…"
              />
            </div>
          </>
        )}
        <div className="flex items-center gap-3 mt-2">
          <Switch
            data-testid={`admin-page-${slug}-published`}
            checked={published}
            onCheckedChange={(v) => setPublished(!!v)}
          />
          <Label className="text-sm text-slate-700">
            {published ? "Pubblicato (visibile agli utenti)" : "Bozza — mostra il contenuto predefinito o il placeholder"}
          </Label>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid={`admin-page-${slug}-save`}
            onClick={save}
            disabled={saving}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Save size={14} className="mr-1.5" /> {saving ? "Salvataggio…" : "Salva tutte le lingue"}
          </Button>
          <Link
            to={`/${slug === "chi-siamo" ? "chi-siamo" : "missione"}`}
            target="_blank"
            data-testid={`admin-page-${slug}-preview`}
            className="inline-flex items-center h-10 px-4 rounded-full bg-white border border-slate-200 text-slate-700 text-sm hover:bg-slate-50"
          >
            Anteprima
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function AdminContentPages() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-content-pages">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <BackButton />
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-3 mb-4">
          Pagine di contenuto
        </h1>
        <p className="text-sm text-slate-600 mb-4">
          Modifica il testo di "La nostra missione" e "Chi siamo" per ognuna delle 6 lingue supportate.
          L'italiano è la base: se una lingua non è compilata, gli utenti vedranno automaticamente il testo italiano.
          Nessuna traduzione automatica — ogni versione è scritta manualmente da te.
        </p>
        {SLUGS.map(({ slug, label }) => (
          <PageEditor key={slug} slug={slug} label={label} />
        ))}
      </div>
    </div>
  );
}
